import axios from "../../../utils/axios";
import { Formik, useFormikContext } from "formik";
import {
  Alert as MuiAlert,
  AlertTitle,
  Box,
  Button as MuiButton,
  CardContent,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import React from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { spacing, SpacingProps } from "@mui/system";
import { useCustomerPaymentInfo } from "../index";
import { useNavigate } from "react-router-dom";
import { usePaymentInputs } from "react-payment-inputs";
import images from "react-payment-inputs/images";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import qs from "qs";
import { PaymentInfoType } from "./types";
import {
  ConfirmOrderArrowIcon,
  formatAudAmount,
  OneSolomonAlert,
  OneSolomonConfirmButton,
  OneSolomonField,
  OneSolomonFieldError,
  OneSolomonFieldLabel,
  OneSolomonFieldRow,
  OneSolomonForm,
  OneSolomonHostedField,
  OneSolomonInput,
  OneSolomonPaymentPage,
  OneSolomonRecaptchaNote,
  OneSolomonSpinner,
  resolveIsCompanyOneSolomons,
} from "../oneSolomonUi";

const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);
interface ButtonProps extends SpacingProps {
  component?: string;
}

const Button = styled(MuiButton)<ButtonProps>(spacing);

const testCases = (amount: number) => [
  {
    status: "Success",
    cardType: "MasterCard",
    cardNumber: "5123456789012346",
    expiry: "12/50",
    cvc: "123",
    name: "Jim Test",
    amount: Math.ceil(amount),
  },
  {
    status: "Declined",
    cardType: "MasterCard",
    cardNumber: "5123456789012346",
    expiry: "12/50",
    cvc: "123",
    name: "Jim Test",
    amount: Math.ceil(amount) + 0.12,
  },
  {
    status: "50s timeout",
    cardType: "MasterCard",
    cardNumber: "5123456789012346",
    expiry: "12/50",
    cvc: "123",
    name: "Jim Test",
    amount: 111.99,
  },
  {
    status: "Success",
    cardType: "MasterCard 2 Series",
    cardNumber: "2720010040360012",
    expiry: "12/50",
    cvc: "123",
    name: "Jim Test",
    amount: Math.ceil(amount),
  },
  {
    status: "Declined",
    cardType: "MasterCard 2 Series",
    cardNumber: "2720010040360012",
    expiry: "12/50",
    cvc: "123",
    name: "Jim Test",
    amount: Math.ceil(amount) + 0.12,
  },
  {
    status: "50s timeout",
    cardType: "MasterCard 2 Series",
    cardNumber: "2720010040360012",
    expiry: "12/50",
    cvc: "123",
    name: "Jim Test",
    amount: 111.99,
  },
];

const BpointTestModeTable = ({
  amount,
  setValues,
}: {
  amount: string | undefined;
  setValues: (values: PaymentInfoType) => void;
}) => (
  <TableContainer component={Paper} sx={{ mt: 6 }}>
    <Table size="small" sx={{ minWidth: 650 }} aria-label="simple table">
      <TableHead>
        <TableRow>
          <TableCell>Status</TableCell>
          <TableCell align="right">Card Type</TableCell>
          <TableCell align="right">Card #</TableCell>
          <TableCell align="right">Expiry</TableCell>
          <TableCell align="right">CVC</TableCell>
          <TableCell align="right">Choose</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {testCases(Number(amount))?.map((testCase, i) => (
          <TableRow
            key={i}
            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              {testCase.status}
            </TableCell>
            <TableCell align="right">{testCase.cardType}</TableCell>
            <TableCell align="right">{testCase.cardNumber}</TableCell>
            <TableCell align="right">{testCase.expiry}</TableCell>
            <TableCell align="right">{testCase.cvc}</TableCell>
            <TableCell align="right">
              <Button
                onClick={() => {
                  setValues({
                    card_holder_name: testCase.name,
                    cardNumber: testCase.cardNumber,
                    cvc: testCase.cvc,
                    expiryDate: testCase.expiry,
                    currency: "AUD",
                    amount: testCase.amount.toString(),
                  });
                }}
              >
                Select
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const DefaultBpointFormFields = () => {
  const { values: customerInfo } = useCustomerPaymentInfo();
  const {
    values,
    setValues,
    handleSubmit,
    touched,
    errors,
    handleBlur,
    handleChange,
  } = useFormikContext<PaymentInfoType>();

  const {
    meta: { erroredInputs, touchedInputs },
    getCardImageProps,
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
  } = usePaymentInputs({
    autoFocus: false,
  });

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        name="amount"
        label="Amount"
        value={values.amount}
        error={Boolean(touched.amount && errors.amount)}
        fullWidth
        helperText={touched.amount && errors.amount}
        onBlur={handleBlur}
        onChange={handleChange}
        variant="outlined"
        margin="normal"
      />
      <TextField
        name="card_holder_name"
        label="Card Holder Name"
        value={values.card_holder_name}
        error={Boolean(touched.card_holder_name && errors.card_holder_name)}
        fullWidth
        helperText={touched.card_holder_name && errors.card_holder_name}
        onBlur={handleBlur}
        onChange={handleChange}
        variant="outlined"
        margin="normal"
      />
      <TextField
        label="Card Number"
        fullWidth
        error={Boolean(
          (touchedInputs.cardNumber || touched.cardNumber) &&
            (erroredInputs.cardNumber || errors.cardNumber)
        )}
        helperText={
          (touchedInputs.cardNumber || touched.cardNumber) &&
          (erroredInputs.cardNumber ?? errors.cardNumber)
        }
        onBlur={handleBlur}
        onChange={handleChange}
        variant="outlined"
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {/* @ts-ignore */}
              <svg {...getCardImageProps({ images })} />
            </InputAdornment>
          ),
        }}
        inputProps={getCardNumberProps({
          value: values.cardNumber,
          onChange: handleChange,
          onBlur: handleBlur,
        })}
      />
      <Grid container spacing={12}>
        <Grid size={6}>
          <TextField
            label="Expiry"
            fullWidth
            error={Boolean(
              (touchedInputs.expiryDate || touched.expiryDate) &&
                (erroredInputs.expiryDate || errors.expiryDate)
            )}
            helperText={
              (touchedInputs.expiryDate || touched.expiryDate) &&
              (erroredInputs.expiryDate ?? errors.expiryDate)
            }
            onBlur={handleBlur}
            onChange={handleChange}
            variant="outlined"
            margin="normal"
            value={values.expiryDate}
            inputProps={getExpiryDateProps({
              onChange: handleChange,
              onBlur: handleBlur,
            })}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label="CVC"
            fullWidth
            error={Boolean(
              (touchedInputs.cvc || touched.cvc) &&
                (erroredInputs.cvc || errors.cvc)
            )}
            helperText={
              (touchedInputs.cvc || touched.cvc) &&
              (erroredInputs.cvc ?? errors.cvc)
            }
            onBlur={handleBlur}
            onChange={handleChange}
            variant="outlined"
            margin="normal"
            value={values.cvc}
            inputProps={getCVCProps({
              onChange: handleChange,
              onBlur: handleBlur,
            })}
          />
        </Grid>
      </Grid>
      <Button type="submit" variant="contained" color="primary" mt={3}>
        Pay
      </Button>
      {Boolean(customerInfo?.testMode) && (
        <BpointTestModeTable amount={values.amount} setValues={setValues} />
      )}
    </form>
  );
};

const OneSolomonBpointFormFields = () => {
  const { values: customerInfo } = useCustomerPaymentInfo();
  const {
    values,
    setValues,
    handleSubmit,
    touched,
    errors,
    handleBlur,
    handleChange,
  } = useFormikContext<PaymentInfoType>();

  const {
    meta: { erroredInputs, touchedInputs },
    getCardImageProps,
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
  } = usePaymentInputs({
    autoFocus: false,
  });

  return (
    <OneSolomonForm onSubmit={handleSubmit} noValidate>
      <OneSolomonField>
        <OneSolomonFieldLabel htmlFor="amount">Amount</OneSolomonFieldLabel>
        <OneSolomonInput
          id="amount"
          name="amount"
          type="text"
          value={formatAudAmount(Number(values.amount) || 0)}
          readOnly
        />
        {touched.amount && errors.amount && (
          <OneSolomonFieldError>{errors.amount}</OneSolomonFieldError>
        )}
      </OneSolomonField>

      <OneSolomonField>
        <OneSolomonFieldLabel htmlFor="card_holder_name">
          Card holder name
        </OneSolomonFieldLabel>
        <OneSolomonInput
          id="card_holder_name"
          name="card_holder_name"
          type="text"
          value={values.card_holder_name}
          onBlur={handleBlur}
          onChange={handleChange}
          autoComplete="cc-name"
          hasError={Boolean(
            touched.card_holder_name && errors.card_holder_name
          )}
        />
        {touched.card_holder_name && errors.card_holder_name && (
          <OneSolomonFieldError>{errors.card_holder_name}</OneSolomonFieldError>
        )}
      </OneSolomonField>

      <OneSolomonField>
        <OneSolomonFieldLabel htmlFor="cardNumber">Card number</OneSolomonFieldLabel>
        <OneSolomonHostedField>
          {/* @ts-ignore */}
          <svg {...getCardImageProps({ images })} />
          <OneSolomonInput
            borderless
            hasError={Boolean(
              (touchedInputs.cardNumber || touched.cardNumber) &&
                (erroredInputs.cardNumber || errors.cardNumber)
            )}
            {...getCardNumberProps({
              value: values.cardNumber,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
          />
        </OneSolomonHostedField>
        {(touchedInputs.cardNumber || touched.cardNumber) &&
          (erroredInputs.cardNumber || errors.cardNumber) && (
            <OneSolomonFieldError>
              {erroredInputs.cardNumber ?? errors.cardNumber}
            </OneSolomonFieldError>
          )}
      </OneSolomonField>

      <OneSolomonFieldRow>
        <OneSolomonField inRow>
          <OneSolomonFieldLabel htmlFor="expiryDate">Expiry</OneSolomonFieldLabel>
          <OneSolomonHostedField>
            <OneSolomonInput
              borderless
              value={values.expiryDate}
              hasError={Boolean(
                (touchedInputs.expiryDate || touched.expiryDate) &&
                  (erroredInputs.expiryDate || errors.expiryDate)
              )}
              {...getExpiryDateProps({
                onChange: handleChange,
                onBlur: handleBlur,
              })}
            />
          </OneSolomonHostedField>
          {(touchedInputs.expiryDate || touched.expiryDate) &&
            (erroredInputs.expiryDate || errors.expiryDate) && (
              <OneSolomonFieldError>
                {erroredInputs.expiryDate ?? errors.expiryDate}
              </OneSolomonFieldError>
            )}
        </OneSolomonField>

        <OneSolomonField inRow>
          <OneSolomonFieldLabel htmlFor="cvc">CVC</OneSolomonFieldLabel>
          <OneSolomonHostedField>
            <OneSolomonInput
              borderless
              value={values.cvc}
              hasError={Boolean(
                (touchedInputs.cvc || touched.cvc) &&
                  (erroredInputs.cvc || errors.cvc)
              )}
              {...getCVCProps({
                onChange: handleChange,
                onBlur: handleBlur,
              })}
            />
          </OneSolomonHostedField>
          {(touchedInputs.cvc || touched.cvc) &&
            (erroredInputs.cvc || errors.cvc) && (
              <OneSolomonFieldError>
                {erroredInputs.cvc ?? errors.cvc}
              </OneSolomonFieldError>
            )}
        </OneSolomonField>
      </OneSolomonFieldRow>

      <OneSolomonConfirmButton type="submit" style={{ marginTop: 18 }}>
        <span>Pay</span>
        <ConfirmOrderArrowIcon />
      </OneSolomonConfirmButton>

      <OneSolomonRecaptchaNote>
        Protected by reCAPTCHA.{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy
        </a>{" "}
        &middot;{" "}
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms
        </a>
      </OneSolomonRecaptchaNote>

      {Boolean(customerInfo?.testMode) && (
        <BpointTestModeTable amount={values.amount} setValues={setValues} />
      )}
    </OneSolomonForm>
  );
};

const BpointPaymentForm = () => {
  const navigate = useNavigate();
  const { values: customerInfo } = useCustomerPaymentInfo();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isCompanyOneSolomons = resolveIsCompanyOneSolomons(
    customerInfo?.company_uuid,
    customerInfo?.isCompanyOneSolomons
  );

  const initialValues: PaymentInfoType = {
    currency: "AUD",
    cardNumber: "",
    cvc: "",
    expiryDate: "",
    card_holder_name: "",
    amount: customerInfo?.amount?.toString(),
  };

  const validationSchema = Yup.object().shape({
    currency: Yup.string().required("Required"),
    cardNumber: Yup.string().required("Required"),
    cvc: Yup.string().required("Required"),
    expiryDate: Yup.string().required("Required"),
    card_holder_name: Yup.string().required("Required"),
    amount: Yup.number()
      .min(Number(customerInfo?.amount) || 0)
      .max(Number(customerInfo?.totalQuoteAmount) || 0)
      .required("Required"),
  });

  const handleSubmit = async (
    values: PaymentInfoType,
    { setErrors, setStatus, setSubmitting }: any
  ) => {
    if (!executeRecaptcha) {
      console.error(
        "handleSubmit() was called even though executeRecaptcha is not yet available"
      );
      return;
    }

    try {
      await axios.post("/api/verify-recaptcha-token", {
        "g-recaptcha-response": await executeRecaptcha(
          "PaymentForm_handleSubmit"
        ),
      });
    } catch (error: any) {
      let msg = "Something went wrong. Please try a again.";
      if (typeof error === "string") msg = error;
      else if (error?.message) msg = error?.message;

      setStatus({ sent: false });
      setErrors({ submit: msg });
      setSubmitting(false);
      return;
    }

    const expiryDate = values.expiryDate.split("/").map((v) => v.trim());

    let result: any = null;
    try {
      result = await new Promise((resolve, reject) => {
        performance.mark("CBA.ProcessPayment:started");
        console.log("CBA.ProcessPayment started");

        // @ts-ignore
        CBA.ProcessPayment({
          AuthKey: customerInfo?.authKey,
          Crn1: customerInfo?.merchantReference,
          Crn2: `${customerInfo?.firstName} ${customerInfo?.lastName}`,
          Crn3: customerInfo?.totalQuoteAmount,
          MerchantReference: customerInfo?.merchantReference,
          EmailAddress: customerInfo?.email,
          Currency: values.currency,
          CardNumber: values.cardNumber,
          Cvn: values.cvc,
          ExpiryMonth: expiryDate[0],
          ExpiryYear: expiryDate[1],
          Amount: values?.amount ?? 0,
          CardHolderName: values.card_holder_name,
          CallbackFunction: (result: any) => {
            const measurement = performance.measure(
              "CBA.ProcessPayment:duration",
              {
                start: `CBA.ProcessPayment:started`,
              }
            );

            if (measurement.duration > 6000) {
              console.log(
                "CBA.ProcessPayment took longer than expected (6s):",
                `${measurement.duration.toFixed(2)} ms`
              );
            }

            console.log("CBA.ProcessPayment result:", result);

            if (result.AjaxResponseType == 0) {
              if (result.ApiResponseCode == 0) {
                resolve(result);
              } else if (result.ApiResponseCode == 300) {
                if (
                  result.RedirectionUrl != null &&
                  result.RedirectionUrl.length > 0
                ) {
                  setTimeout(() => {
                    window.location.href = result.RedirectionUrl;
                  }, 5000);
                } else {
                  reject(result);
                }
              } else {
                reject(result);
              }
            } else if (result.AjaxResponseType == 1) {
              reject(result);
            } else if (result.AjaxResponseType == 2) {
              reject(result);
            }
          },
        });
      });

      const url = new URL(result.RedirectionUrl);
      const companyUuid = customerInfo?.company_uuid;
      if (typeof companyUuid === "string" && companyUuid.trim()) {
        url.searchParams.set("company_uuid", companyUuid.trim());
      }

      setTimeout(() => {
        navigate(`/payment-result${url.search}`, { replace: true });
      }, 5000);
    } catch (errors: any) {
      if (errors.AjaxResponseType === 0 && errors.ApiResponseCode === 206) {
        setTimeout(() => {
          navigate(
            `/${qs.stringify(
              {
                initialValues: customerInfo,
              },
              { addQueryPrefix: true }
            )}`,
            { replace: true }
          );
        }, 1000);
      }

      setStatus({ sent: false });
      setErrors(errors);
      setSubmitting(false);
    }
  };

  const renderAlerts = (errors: any) => (
    <>
      {Boolean(customerInfo?.testMode) && (
        isCompanyOneSolomons ? (
          <OneSolomonAlert variant="warning">
            <strong>TEST MODE</strong>
            {customerInfo?.testModeReason
              ? `: ${customerInfo.testModeReason}`
              : ""}
            <br />
            Payments will be marked as TEST mode.
          </OneSolomonAlert>
        ) : (
          <Alert severity="warning" my={4}>
            <AlertTitle>
              TEST MODE{" "}
              {!!customerInfo?.testModeReason &&
                `: ${customerInfo?.testModeReason}`}
            </AlertTitle>
            Payments will be marked as TEST mode.
          </Alert>
        )
      )}

      {errors?.submit &&
        (isCompanyOneSolomons ? (
          <OneSolomonAlert variant="error">{errors.submit}</OneSolomonAlert>
        ) : (
          <Alert severity="error" my={3}>
            {errors.submit}
          </Alert>
        ))}

      {errors?.Errors?.map((err: any, index: number) =>
        isCompanyOneSolomons ? (
          <OneSolomonAlert variant="error" key={index}>
            {err.Message}
          </OneSolomonAlert>
        ) : (
          <Alert severity="error" my={3} key={index}>
            {err.Message}
          </Alert>
        )
      )}
    </>
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, isSubmitting }) =>
        isCompanyOneSolomons ? (
          <OneSolomonPaymentPage title="Credit Card Payment">
            {renderAlerts(errors)}
            {isSubmitting ? (
              <OneSolomonSpinner />
            ) : (
              <OneSolomonBpointFormFields />
            )}
          </OneSolomonPaymentPage>
        ) : (
          <>
            <Typography component="h1" variant="h1" align="center">
              Credit Card Payment
            </Typography>
            <CardContent>
              {renderAlerts(errors)}
              {isSubmitting ? (
                <Box display="flex" justifyContent="center" my={6}>
                  <CircularProgress />
                </Box>
              ) : (
                <DefaultBpointFormFields />
              )}
            </CardContent>
          </>
        )
      }
    </Formik>
  );
};

export default BpointPaymentForm;
